/**
 * Sound to Shape Game
 * Listen to the sound and choose the correct letter pattern
 */
import { useState, useEffect, useCallback } from 'react';
import { PhonicsRule } from '../../../data/natural-phonics';
import { getWordEmoji } from '../../../data/phonics-images';
import { scoringRules } from '../../../data/phonics-games';

interface SoundToShapeGameProps {
  rule: PhonicsRule;
  style: 'american' | 'british';
  onComplete: (score: number, stars: number) => void;
  onExit: () => void;
}

interface Question {
  sound: string;
  correctPattern: string;
  options: string[];
  word: string;
  meaning: string;
  image: string;
}

export default function SoundToShapeGame({
  rule,
  style,
  onComplete,
  onExit,
}: SoundToShapeGameProps) {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);

  // Generate questions
  useEffect(() => {
    const generatedQuestions = generateQuestions(rule, style);
    setQuestions(generatedQuestions);
  }, [rule, style]);

  const currentQuestion = questions[currentIndex];
  const totalQuestions = questions.length;

  // Speak the sound
  const speakSound = useCallback(() => {
    if (!currentQuestion) return;

    const utterance = new SpeechSynthesisUtterance(currentQuestion.sound);
    utterance.lang = style === 'american' ? 'en-US' : 'en-GB';
    utterance.rate = 0.7;
    speechSynthesis.speak(utterance);
  }, [currentQuestion, style]);

  // Speak on question change
  useEffect(() => {
    if (currentQuestion) {
      setTimeout(() => speakSound(), 300);
    }
  }, [currentQuestion, speakSound]);

  // Handle option selection
  const handleSelect = (option: string) => {
    if (feedback || !currentQuestion) return;

    setSelectedOption(option);
    const isCorrect = option === currentQuestion.correctPattern;

    if (isCorrect) {
      setFeedback('correct');
      setStreak(s => s + 1);
      setCorrectCount(c => c + 1);

      // Calculate points
      let points = scoringRules.soundToShape.correct;
      if (streak >= 3) {
        points += scoringRules.soundToShape.streakBonus;
      }
      setScore(s => s + points);
    } else {
      setFeedback('wrong');
      setStreak(0);
    }

    // Move to next question after delay
    setTimeout(() => {
      if (currentIndex < totalQuestions - 1) {
        setCurrentIndex(i => i + 1);
        setFeedback(null);
        setSelectedOption(null);
      } else {
        setShowResult(true);
      }
    }, 1200);
  };

  // Calculate stars
  const calculateStars = (accuracy: number): number => {
    if (accuracy >= 90) return 3;
    if (accuracy >= 70) return 2;
    if (accuracy >= 50) return 1;
    return 0;
  };

  // Handle game completion
  const handleComplete = () => {
    const accuracy = (correctCount / totalQuestions) * 100;
    const stars = calculateStars(accuracy);
    onComplete(score, stars);
  };

  // Show result screen
  if (showResult) {
    const accuracy = Math.round((correctCount / totalQuestions) * 100);
    const stars = calculateStars(accuracy);

    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-blue-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center animate-bounce-in">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">游戏完成！</h2>

          {/* Stars */}
          <div className="flex justify-center gap-2 my-6">
            {[1, 2, 3].map(i => (
              <span
                key={i}
                className={`text-4xl ${i <= stars ? 'animate-star-fly' : 'opacity-30'}`}
                style={{ animationDelay: `${i * 0.2}s` }}
              >
                ⭐
              </span>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 my-6">
            <div className="bg-blue-50 rounded-xl p-4">
              <p className="text-2xl font-bold text-blue-600">{score}</p>
              <p className="text-sm text-gray-500">得分</p>
            </div>
            <div className="bg-green-50 rounded-xl p-4">
              <p className="text-2xl font-bold text-green-600">{correctCount}/{totalQuestions}</p>
              <p className="text-sm text-gray-500">正确</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-4">
              <p className="text-2xl font-bold text-purple-600">{accuracy}%</p>
              <p className="text-sm text-gray-500">正确率</p>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-4">
            <button
              onClick={onExit}
              className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
            >
              返回
            </button>
            <button
              onClick={handleComplete}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-xl font-bold hover:shadow-lg transition-all"
            >
              确认
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (!currentQuestion) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-blue-50 flex items-center justify-center">
        <div className="text-2xl text-gray-500">加载中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-blue-50 p-6">
      {/* Header */}
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={onExit}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <span className="text-2xl">←</span>
            <span className="font-medium">退出</span>
          </button>

          <div className="flex items-center gap-4">
            <div className="bg-white px-4 py-2 rounded-xl shadow-soft">
              <span className="text-lg font-bold text-primary">{score}</span>
              <span className="text-sm text-gray-500 ml-1">分</span>
            </div>
            {streak >= 3 && (
              <div className="bg-orange-100 px-3 py-2 rounded-xl animate-pulse-glow">
                <span className="text-lg font-bold text-orange-600">🔥 {streak}</span>
              </div>
            )}
          </div>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-500 mb-2">
            <span>第 {currentIndex + 1} 题</span>
            <span>共 {totalQuestions} 题</span>
          </div>
          <div className="h-3 bg-white rounded-full overflow-hidden shadow-inner">
            <div
              className="h-full bg-gradient-to-r from-green-400 to-teal-400 transition-all duration-300"
              style={{ width: `${((currentIndex + 1) / totalQuestions) * 100}%` }}
            />
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-3xl shadow-lg p-8 mb-6">
          {/* Sound Button */}
          <div className="text-center mb-8">
            <p className="text-gray-500 mb-4">听发音，选择正确的字母组合</p>
            <button
              onClick={speakSound}
              className="w-32 h-32 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-6xl shadow-xl hover:shadow-2xl transition-all hover:scale-105 active:scale-95"
            >
              🔊
            </button>
            <p className="mt-4 text-sm text-gray-400">点击播放发音</p>
          </div>

          {/* Options */}
          <div className="grid grid-cols-2 gap-4">
            {currentQuestion.options.map((option, idx) => {
              const isSelected = selectedOption === option;
              const isCorrect = option === currentQuestion.correctPattern;
              const showFeedback = feedback && isSelected;

              let bgClass = 'bg-gray-50 hover:bg-gray-100';
              if (showFeedback) {
                bgClass = isCorrect
                  ? 'bg-green-100 border-2 border-green-400 animate-correct-flash'
                  : 'bg-red-100 border-2 border-red-400 animate-wrong-shake';
              } else if (feedback && isCorrect) {
                bgClass = 'bg-green-100 border-2 border-green-400';
              }

              return (
                <button
                  key={idx}
                  onClick={() => handleSelect(option)}
                  disabled={!!feedback}
                  className={`${bgClass} rounded-2xl p-6 transition-all ${
                    !feedback ? 'hover:scale-105 cursor-pointer' : ''
                  }`}
                >
                  <span className="text-2xl font-bold text-gray-800">{option}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Feedback with word */}
        {feedback && (
          <div
            className={`bg-white rounded-2xl shadow-lg p-6 text-center animate-slide-up ${
              feedback === 'correct' ? 'border-2 border-green-400' : 'border-2 border-red-400'
            }`}
          >
            <div className="flex items-center justify-center gap-4">
              <span className="text-5xl">{currentQuestion.image}</span>
              <div className="text-left">
                <p className="text-2xl font-bold text-gray-800">{currentQuestion.word}</p>
                <p className="text-gray-500">{currentQuestion.meaning}</p>
              </div>
              <span className="text-3xl">{feedback === 'correct' ? '✅' : '❌'}</span>
            </div>
            {feedback === 'correct' && streak >= 3 && (
              <p className="mt-3 text-orange-500 font-bold">🔥 连击奖励 +{scoringRules.soundToShape.streakBonus}分!</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Generate questions for the game
function generateQuestions(rule: PhonicsRule, _style: 'american' | 'british'): Question[] {
  const questions: Question[] = [];

  // Get all patterns from the same category
  const patterns = [rule.pattern];

  // Add some similar patterns as distractors
  const distractors = getDistractorPatterns(rule.pattern, rule.category);
  patterns.push(...distractors.slice(0, 3));

  // Create questions from examples
  for (const example of rule.examples) {
    const allOptions = [...patterns];
    // Shuffle options
    const shuffledOptions = allOptions.sort(() => Math.random() - 0.5).slice(0, 4);

    // Ensure correct answer is included
    if (!shuffledOptions.includes(rule.pattern)) {
      shuffledOptions[0] = rule.pattern;
    }

    questions.push({
      sound: example.word,
      correctPattern: rule.pattern,
      options: shuffledOptions.sort(() => Math.random() - 0.5),
      word: example.word,
      meaning: example.meaning,
      image: getWordEmoji(example.word),
    });
  }

  return questions;
}

// Get distractor patterns based on category
function getDistractorPatterns(correctPattern: string, _category: string): string[] {
  const allPatterns = [
    'a', 'e', 'i', 'o', 'u',
    'a_e', 'i_e', 'o_e', 'u_e',
    'ee', 'ea', 'ai', 'ay', 'oa', 'oo', 'igh',
    'sh', 'ch', 'th', 'ng', 'ck',
    'bl', 'cl', 'fl', 'gl', 'pl', 'sl',
    'br', 'cr', 'dr', 'fr', 'gr', 'pr', 'tr',
  ];

  // Filter out the correct pattern and return random ones
  return allPatterns
    .filter(p => p !== correctPattern)
    .sort(() => Math.random() - 0.5)
    .slice(0, 5);
}
